import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useAddJobRoleExpanderMutation } from '../../apis/use-add-job-role-expander-mutation';
import { useJobTitleExpanderStore } from '../../store';
import { CombineEntriesCheckbox } from '../combine-entries-checkbox';
import { SelectedTitlesHeader } from '../selected-titles-header/selected-titles-header';
import { SelectedTitlesInput } from '../selected-titles-input';
import * as Styled from './styles';
import * as SX from './sx';
import type { SelectedTitlesProps as Props } from './types';

export const SelectedTitles = memo(({ className, ...props }: Props) => {
  // Use store
  const {
    selectedValues,
    clearSelectedValues,
    combine,
    setCombine,
    isLoading,
    setIsLoading,
    setData,
  } = useJobTitleExpanderStore();

  const api = useAddJobRoleExpanderMutation();

  const handleClearValues = useCallback(() => {
    clearSelectedValues();
  }, [clearSelectedValues]);

  const handleGenerateSimilarTitles = useCallback(() => {
    setIsLoading(true);
    api
      .mutateAsync({
        titles: selectedValues,
        combine: combine,
      })
      .then((response) => {
        setData(response);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [api, selectedValues, combine, setData, setIsLoading]);

  const buttonText = useMemo(() => {
    return isLoading ? 'Generating ...' : 'Generate Similar Titles';
  }, [isLoading]);

  return (
    <Styled.Wrapper
      className={classnames('SelectedTitles', className)}
      sx={SX.root}
    >
      <Stack gap={1}>
        <SelectedTitlesHeader
          count={selectedValues.length}
          onClick={handleClearValues}
        />

        <SelectedTitlesInput {...props} />

        <CombineEntriesCheckbox onChange={setCombine} />
      </Stack>
      <Button
        startIcon={<Icon name="outline-auto-awesome" />}
        variant="outlined"
        color="default"
        size="small"
        disabled={!selectedValues.length || isLoading}
        onClick={handleGenerateSimilarTitles}
      >
        {buttonText}
      </Button>
    </Styled.Wrapper>
  );
});

SelectedTitles.displayName = 'SelectedTitles';
